import { apiClient, notifyAuthRotation, setAccessToken } from "./api-client";
import type {
  CreateFamilyRequest,
  CreateFamilyResponse,
  DeactivateFamilyMemberResponse,
  Family,
  FamilyListItem,
  FamilyMember,
  FamilyPreview,
  FamilyResponse,
  JoinFamilyRequest,
  JoinFamilyResponse,
  UpdateFamilyMemberResponse,
} from "../types/family";

interface ApiResponse<T> {
  data: T;
}

/**
 * Stores the rotated access token returned by family membership commands and
 * notifies the AuthProvider so React state stays in sync. The new refresh
 * token rides on the HttpOnly cookie set by the backend — the frontend can
 * neither read it nor needs to.
 */
function applyRotatedTokens(tokens: {
  access_token: string;
  expires_in: number;
}): void {
  setAccessToken(tokens.access_token);
  notifyAuthRotation({
    access_token: tokens.access_token,
    expires_in: tokens.expires_in,
  });
}

export function createFamily(name: string): Promise<Family> {
  const body: CreateFamilyRequest = { name };
  return apiClient
    .post<ApiResponse<CreateFamilyResponse>>("/families", body)
    .then((res) => {
      applyRotatedTokens(res.data);
      return res.data.family;
    });
}

export function getFamily(id: string): Promise<FamilyResponse> {
  return apiClient
    .get<ApiResponse<FamilyResponse>>(`/families/${id}`)
    .then((res) => res.data);
}

/** Rotates the family's invite code (admin only). Returns the updated family. */
export function regenerateInvite(familyId: string): Promise<FamilyResponse> {
  return apiClient
    .post<ApiResponse<FamilyResponse>>(`/families/${familyId}/invite`)
    .then((res) => res.data);
}

/**
 * Public lookup of a family by its invite code. Used by the join screen to
 * display the family name before the user authenticates or joins.
 */
export function getFamilyByInviteCode(code: string): Promise<FamilyPreview> {
  const query = new URLSearchParams({ code }).toString();
  return apiClient
    .get<ApiResponse<FamilyPreview>>(`/families/by-invite?${query}`)
    .then((res) => res.data);
}

/** Joins the authenticated user to a family via invite code. */
export function joinFamily(
  inviteCode: string,
  nickname: string
): Promise<Family> {
  const body: JoinFamilyRequest = {
    invite_code: inviteCode,
    nickname,
  };
  return apiClient
    .post<ApiResponse<JoinFamilyResponse>>("/families/join", body)
    .then((res) => {
      applyRotatedTokens(res.data);
      return res.data.family;
    });
}

export function getMyFamilies(): Promise<FamilyListItem[]> {
  return apiClient
    .get<ApiResponse<FamilyListItem[]>>("/families/mine")
    .then((res) => res.data);
}

export function getFamilyMembers(familyId: string): Promise<FamilyMember[]> {
  return apiClient.get<ApiResponse<FamilyMember[]>>(`/families/${familyId}/members`).then((res) => res.data);
}

export function updateFamilyNickname(familyId: string, nickname: string): Promise<UpdateFamilyMemberResponse> {
  return apiClient
    .patch<ApiResponse<UpdateFamilyMemberResponse>>(`/families/${familyId}/members/me`, { nickname })
    .then((res) => res.data);
}

export function deactivateFamilyMember(familyId: string, memberId: string): Promise<DeactivateFamilyMemberResponse> {
  return apiClient
    .delete<ApiResponse<DeactivateFamilyMemberResponse>>(`/families/${familyId}/members/${memberId}`)
    .then((res) => {
      if (res.data.access_token && res.data.expires_in) applyRotatedTokens({
        access_token: res.data.access_token,
        expires_in: res.data.expires_in,
      });
      return res.data;
    });
}
